import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";

export default function ContaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [aba, setAba] = useState("dados");
  const [loading, setLoading] = useState(isEdit);

  const [conta, setConta] = useState({
    nome: "",
    nomeFantasia: "",
    documento: "",
    tipoPessoa: 1,
    quantidadeMaximaDeClientes: 0
  });

  const [administrador, setAdministrador] = useState({
    nome: "",
    email: "",
    senha: "",
    confirmarSenha: "",
  });

  // Usuários da conta (apenas edição)
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [filtroUsuarioAtivo, setFiltroUsuarioAtivo] = useState("true");

  // Modal de novo usuário
  const [showModal, setShowModal] = useState(false);
  const [novoUsuario, setNovoUsuario] = useState({
    nome: "",
    email: "",
    senha: "",
    tipoUsuario: 3,
  });
  const [salvandoUsuario, setSalvandoUsuario] = useState(false);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${process.env.REACT_APP_API}/conta/${id}`)
        .then((res) => {
          const data = res.data.data;
          setConta({
            nome: data.nome || "",
            nomeFantasia: data.nomeFantasia || "",
            documento: data.documento || "",
            tipoPessoa: data.tipoPessoa ?? 1,
            quantidadeMaximaDeClientes: data.quantidadeMaximaDeClientes ?? "0",
          });
        })
        .catch((err) => {
          console.error(err);
          AlertComponent("Erro", "Erro ao carregar conta", { icon: "error" });
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  // Carregar usuários quando mudar para aba de usuários ou mudar o filtro
  useEffect(() => {
    if (isEdit && aba === "usuarios") {
      fetchUsuarios();
    }
  }, [isEdit, aba, filtroUsuarioAtivo]);

  const fetchUsuarios = async () => {
    setLoadingUsuarios(true);
    try {
      const params = new URLSearchParams();
      if (filtroUsuarioAtivo !== "") {
        params.set("ativo", filtroUsuarioAtivo);
      }
      const queryString = params.toString();
      const url = `${process.env.REACT_APP_API}/conta/${id}/usuarios${queryString ? `?${queryString}` : ""}`;
      const res = await axios.get(url);
      setUsuarios(res.data.data || []);
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao carregar usuários", { icon: "error" });
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const formatarCPF = (valor) => {
    return valor
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  const formatarCNPJ = (valor) => {
    return valor
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  const formatarDocumento = (valor, tipoPessoa) => {
    if (tipoPessoa === 0 || tipoPessoa === "0") {
      return formatarCPF(valor);
    }
    return formatarCNPJ(valor);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "documento") {
      setConta((prev) => ({
        ...prev,
        [name]: formatarDocumento(value, prev.tipoPessoa),
      }));
    } else if (name === "tipoPessoa") {
      setConta((prev) => ({
        ...prev,
        [name]: parseInt(value),
        documento: "",
      }));
    } else if (name === "quantidadeMaximaDeClientes") {
      const intValue = value === '' ? 0 : parseInt(value, 10);
      if (!isNaN(intValue) && intValue >= 0) {
        setConta((prev) => ({ ...prev, [name]: intValue }));
      }
    } else {
      setConta((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAdminChange = (e) => {
    const { name, value } = e.target;
    setAdministrador((prev) => ({ ...prev, [name]: value }));
  };

  const handleNovoUsuarioChange = (e) => {
    const { name, value } = e.target;
    setNovoUsuario((prev) => ({
      ...prev,
      [name]: name === "tipoUsuario" ? parseInt(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit) {
      if (administrador.senha !== administrador.confirmarSenha) {
        AlertComponent("Erro", "As senhas não coincidem", { icon: "error" });
        return;
      }
      if (administrador.senha.length < 6) {
        AlertComponent("Erro", "A senha deve ter pelo menos 6 caracteres", { icon: "error" });
        return;
      }
    }

    try {
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API}/conta/${id}`, conta);
        AlertComponent("Sucesso", "Conta salva com sucesso!", { icon: "success" });
      } else {
        const payload = {
          ...conta,
          administrador: {
            nome: administrador.nome,
            email: administrador.email,
            senha: administrador.senha,
          },
        };
        await axios.post(`${process.env.REACT_APP_API}/conta`, payload);
        navigate(`/contas?filtro=${encodeURIComponent(conta.nome)}`);
      }
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao salvar conta", { icon: "error" });
    }
  };

  const handleCriarUsuario = async (e) => {
    e.preventDefault();
    
    setSalvandoUsuario(true);
    try {
      await axios.post(`${process.env.REACT_APP_API}/conta/${id}/usuarios`, {
        nome: novoUsuario.nome,
        email: novoUsuario.email,
        senha: novoUsuario.senha,
        tipoUsuario: novoUsuario.tipoUsuario,
      });
      
      AlertComponent("Sucesso", "Usuário criado com sucesso!", { icon: "success" });
      setShowModal(false);
      setNovoUsuario({ nome: "", email: "", senha: "", tipoUsuario: 3 });
      fetchUsuarios();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao criar usuário", { icon: "error" });
    } finally {
      setSalvandoUsuario(false);
    }
  };

  const handleToggleUsuarioStatus = async (usuarioId, ativo) => {
    try {
      await axios.put(`${process.env.REACT_APP_API}/usuario/${usuarioId}/status`, {
        id: usuarioId,
        ativo: !ativo,
      });
      fetchUsuarios();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao alterar status do usuário", { icon: "error" });
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="w-full">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Conta" : "Nova Conta"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit
            ? "Atualize as informações da conta"
            : "Preencha as informações para criar uma nova conta"}
        </p>
      </div>

      {/* ABAS - apenas na edição */}
      {isEdit && (
        <div className="mb-8 border-b">
          <nav className="flex gap-6">
            {[
              { id: "dados", label: "Dados da Conta" },
              { id: "usuarios", label: "Usuários" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setAba(tab.id)}
                className={`pb-3 text-sm font-medium transition ${
                  aba === tab.id
                    ? "border-b-2 border-slate-900 text-slate-900"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                {tab.label}
                {tab.id === "usuarios" && usuarios.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                    {usuarios.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* ABA DADOS */}
      {(aba === "dados" || !isEdit) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* DADOS DA CONTA */}
          <div className="bg-white p-8 rounded-xl shadow border">
            <h2 className="text-lg font-medium text-gray-800 mb-6">Dados da Conta</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  name="nome"
                  value={conta.nome}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Nome da conta"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Pessoa
                </label>
                <select
                  name="tipoPessoa"
                  value={conta.tipoPessoa}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  <option value={0}>Pessoa Física</option>
                  <option value={1}>Pessoa Jurídica</option>
                </select>
              </div>

              {conta.tipoPessoa === 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Fantasia
                  </label>
                  <input
                    name="nomeFantasia"
                    value={conta.nomeFantasia}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                    placeholder="Nome fantasia"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {conta.tipoPessoa === 1 ? "CNPJ" : "CPF"}
                </label>
                <input
                  name="documento"
                  value={conta.documento}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder={conta.tipoPessoa === 1 ? "00.000.000/0000-00" : "000.000.000-00"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade Máxima de Clientes <small>(Quantidade 0 não limitará a quantidade de clientes)</small> *
                </label>
                <input
                  type="number"
                  name="quantidadeMaximaDeClientes"
                  value={conta.quantidadeMaximaDeClientes}
                  onChange={handleChange}
                  step="1"
                  min="0"
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                      setConta((prev) => ({ ...prev, quantidadeMaximaDeClientes: 0 }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') {
                      e.preventDefault();
                    }
                  }}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Quantidade máxima de clientes"
                />
              </div>
            </div>
          </div>

          {/* ADMINISTRADOR DA CONTA - apenas na criação */}
          {!isEdit && (
            <div className="bg-white p-8 rounded-xl shadow border">
              <div className="mb-6">
                <h2 className="text-lg font-medium text-gray-800">Administrador da Conta</h2>
                <p className="text-sm text-gray-500">
                  Informe os dados do usuário que será o administrador desta conta
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Administrador *
                  </label>
                  <input
                    name="nome"
                    value={administrador.nome}
                    onChange={handleAdminChange}
                    className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                    placeholder="Nome completo"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={administrador.email}
                    onChange={handleAdminChange}
                    className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                    placeholder="admin@empresa.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha *
                  </label>
                  <input
                    type="password"
                    name="senha"
                    value={administrador.senha}
                    onChange={handleAdminChange}
                    className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar Senha *
                  </label>
                  <input
                    type="password"
                    name="confirmarSenha"
                    value={administrador.confirmarSenha}
                    onChange={handleAdminChange}
                    className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                    placeholder="Repita a senha"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </div>
          )}

          {/* AÇÕES */}
          <div className="flex justify-end gap-3 border-t pt-6">
            <button
              type="button"
              onClick={() => navigate("/contas")}
              className="px-5 py-2 rounded-md border hover:bg-gray-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800"
            >
              {isEdit ? "Salvar alterações" : "Criar conta"}
            </button>
          </div>
        </form>
      )}

      {/* ABA USUÁRIOS - apenas na edição */}
      {isEdit && aba === "usuarios" && (
        <div className="space-y-6">
          {/* Header com filtro e botão de adicionar */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-800">Usuários da Conta</h2>
              <p className="text-sm text-gray-500">Gerencie os usuários que têm acesso a esta conta</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status:</span>
                <select
                  value={filtroUsuarioAtivo}
                  onChange={(e) => setFiltroUsuarioAtivo(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500"
                >
                  <option value="true">Ativos</option>
                  <option value="false">Inativos</option>
                  <option value="">Todos</option>
                </select>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
              >
                Novo Usuário
              </button>
            </div>
          </div>

          {/* Lista de usuários */}
          <div className="bg-white rounded-xl shadow border">
            {loadingUsuarios ? (
              <div className="p-8 text-center text-gray-500">Carregando usuários...</div>
            ) : usuarios.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500 mb-4">
                  {filtroUsuarioAtivo === "true" 
                    ? "Nenhum usuário ativo nesta conta"
                    : filtroUsuarioAtivo === "false"
                    ? "Nenhum usuário inativo nesta conta"
                    : "Nenhum usuário cadastrado nesta conta"}
                </p>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
                >
                  Adicionar primeiro usuário
                </button>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">Ativo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">
                        {usuario.nome}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {usuario.email}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          usuario.tipoUsuario === 2 
                            ? "bg-blue-100 text-blue-700" 
                            : "bg-gray-100 text-gray-700"
                        }`}>
                          {usuario.tipoUsuario === 2 ? "Admin" : "Usuário"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleUsuarioStatus(usuario.id, usuario.ativo)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            usuario.ativo ? "bg-slate-900" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              usuario.ativo ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* MODAL NOVO USUÁRIO */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => !salvandoUsuario && setShowModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Novo Usuário</h3>
            
            <form onSubmit={handleCriarUsuario} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={novoUsuario.nome}
                  onChange={handleNovoUsuarioChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Nome completo"
                  required
                  disabled={salvandoUsuario}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail *
                </label>
                <input
                  type="email"
                  name="email"
                  value={novoUsuario.email}
                  onChange={handleNovoUsuarioChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="usuario@empresa.com"
                  required
                  disabled={salvandoUsuario}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Usuário *
                </label>
                <select
                  name="tipoUsuario"
                  value={novoUsuario.tipoUsuario}
                  onChange={handleNovoUsuarioChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  disabled={salvandoUsuario}
                >
                  <option value={3}>Usuário</option>
                  <option value={2}>Administrador</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm rounded border hover:bg-gray-50 disabled:opacity-50"
                  disabled={salvandoUsuario}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                  disabled={salvandoUsuario}
                >
                  {salvandoUsuario ? "Criando..." : "Criar Usuário"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}